import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import PhotoGallery from './PhotoGallery';
import UploadForm from './UploadForm';
import Venue from './Venue';
import Guestbook from './Guestbook';
import QAndA from './QAndA';
import Toast from './Toast';
import OfflineUploadStatus from './OfflineUploadStatus';
import { NavigationTab } from './ui';
import { loadLogo, saveLogo, loadTheme, saveTheme, loadTabs, saveTabs, DEFAULT_FONT } from '../utils/themeStorage';
import { applyTheme } from '../utils/themeUtils';
import { isInIframe, createMessageHandler, sendMessageToParent, sendSuccessToParent, sendErrorToParent, getAllowedOrigins, MESSAGE_TYPES } from '../utils/iframeCommunication';
import packageJson from '../../package.json';
import { CONFIG_VERSION } from '../config/version';
import { useGallery } from '../context/GalleryContext';
import GuestRejoinHelp from './GuestRejoinHelp';

const Dashboard = () => {
  const { publicConfig } = useGallery();
  const { galleryId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const dashboardBase = `/g/${galleryId}/dashboard`;
  // Default to 'view' (Gallery) if venue is hidden, otherwise 'venue'
  const [activeTab, setActiveTab] = useState('venue');
  const [selectionMode, setSelectionMode] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [pendingUploadPreviews, setPendingUploadPreviews] = useState([]);
  const pendingUploadPreviewsRef = useRef([]);
  const [toast, setToast] = useState(null);

  const inIframe = isInIframe();

  // Initialize logo, text, and floorplan state
  const [currentLogo, setCurrentLogo] = useState('');
  const [weddingText, setWeddingText] = useState('');
  const [currentFloorplan, setCurrentFloorplan] = useState('');
  const [currentMenu, setCurrentMenu] = useState(null);
  const [showVenueTab, setShowVenueTab] = useState(true);
  const [showMediaFilter, setShowMediaFilter] = useState(true);
  const [showGuestbookTab, setShowGuestbookTab] = useState(true);
  const [showQAndATab, setShowQAndATab] = useState(true);

  // Re-hydrate from storage when GalleryContext writes fresh values on publicConfig change.
  useEffect(() => {
    const savedLogoData = loadLogo();
    const savedTheme = loadTheme();
    const savedTabs = loadTabs();
    setCurrentLogo(savedLogoData.logo);
    setWeddingText(savedLogoData.text);
    setCurrentFloorplan(savedTabs.floorplan);
    setCurrentMenu(savedTabs.menu || null);
    setShowVenueTab(savedTabs.showVenueTab !== false);
    setShowMediaFilter(savedTabs.showMediaFilter !== false);
    setShowGuestbookTab(savedTabs.showGuestbookTab !== false);
    setShowQAndATab(savedTabs.showQAndATab !== false);
    const colors = savedTheme.colors || savedTheme;
    const font = savedTheme.font || DEFAULT_FONT;
    applyTheme({ colors, font });
  }, [publicConfig]);

  // Redirect away from hidden tabs
  useEffect(() => {
    if (!showVenueTab && (location.pathname === dashboardBase || location.pathname.includes('/dashboard/venue'))) {
      navigate(`${dashboardBase}/view`, { replace: true });
      setActiveTab('view');
    }
    if (!showGuestbookTab && location.pathname.includes('/dashboard/guestbook')) {
      navigate(`${dashboardBase}/view`, { replace: true });
      setActiveTab('view');
    }
    if (!showQAndATab && location.pathname.includes('/dashboard/qanda')) {
      navigate(`${dashboardBase}/view`, { replace: true });
      setActiveTab('view');
    }
  }, [location.pathname, dashboardBase, navigate, showVenueTab, showGuestbookTab, showQAndATab]);

  // Update active tab when venue tab visibility changes
  useEffect(() => {
    if (!showVenueTab && activeTab === 'venue') {
      setActiveTab('view');
    }
  }, [showVenueTab, activeTab]);

  // Sync activeTab with current route
  useEffect(() => {
    if (location.pathname.includes('/dashboard/venue')) {
      setActiveTab('venue');
    } else if (location.pathname.includes('/dashboard/guestbook')) {
      setActiveTab('guestbook');
    } else if (location.pathname.includes('/dashboard/qanda')) {
      setActiveTab('qanda');
    } else if (location.pathname.includes('/dashboard/upload')) {
      setActiveTab('upload');
    } else if (location.pathname.includes('/dashboard/view')) {
      setActiveTab('view');
    } else {
      // Default based on venue tab visibility
      setActiveTab(showVenueTab ? 'venue' : 'view');
    }
  }, [location.pathname, showVenueTab]);

  useEffect(() => {
    pendingUploadPreviewsRef.current = pendingUploadPreviews;
  }, [pendingUploadPreviews]);

  useEffect(() => {
    return () => {
      pendingUploadPreviewsRef.current.forEach((item) => {
        if (item.previewUrl) {
          URL.revokeObjectURL(item.previewUrl);
        }
      });
    };
  }, []);

  // Handle upload completion
  const handleUploadComplete = useCallback((uploadedFiles = []) => {
    console.log('Upload completed, refreshing photo gallery:', uploadedFiles);
    const timestamp = Date.now();
    const previewItems = uploadedFiles
      .filter((file) => file.preview && (file.status === 'success' || file.status === 'queued'))
      .map((file) => ({
        id: `pending-${file.originalPath || file.id || file.name}`,
        type: file.fileType || (file.contentType?.startsWith('video/') ? 'video' : 'image'),
        filename: file.originalPath || file.name,
        originalPath: file.originalPath || null,
        previewUrl: file.preview,
        thumbnailUrl: file.preview,
        contentType: file.contentType || null,
        processed: false,
        isPendingUpload: true,
        uploadStatus: file.status,
        createdAt: timestamp,
      }));

    if (previewItems.length > 0) {
      setPendingUploadPreviews((currentItems) => {
        const nextItemsByKey = new Map(
          currentItems.map((item) => [item.originalPath || item.filename || item.id, item])
        );
        previewItems.forEach((item) => {
          const key = item.originalPath || item.filename || item.id;
          const existingItem = nextItemsByKey.get(key);
          if (existingItem?.previewUrl && existingItem.previewUrl !== item.previewUrl) {
            URL.revokeObjectURL(existingItem.previewUrl);
          }
          nextItemsByKey.set(key, item);
        });
        return Array.from(nextItemsByKey.values()).slice(0, 20);
      });

      window.setTimeout(() => {
        setPendingUploadPreviews((currentItems) => {
          const previewKeys = new Set(
            previewItems.map((item) => item.originalPath || item.filename || item.id)
          );
          const expiredItems = currentItems.filter((item) =>
            previewKeys.has(item.originalPath || item.filename || item.id)
          );
          expiredItems.forEach((item) => {
            if (item.previewUrl) {
              URL.revokeObjectURL(item.previewUrl);
            }
          });
          return currentItems.filter((item) =>
            !previewKeys.has(item.originalPath || item.filename || item.id)
          );
        });
      }, 10 * 60 * 1000);
    }

    setRefreshTrigger(prev => prev + 1);
  }, []);

  // Memoized callback for upload tab activation
  const handleUploadTabActivate = useCallback(() => {
    setActiveTab('upload');
  }, []);

  // Handle iframe postMessage communication
  useEffect(() => {
    console.log('Dashboard iframe check:', { inIframe, windowSelf: window.self, windowTop: window.top });
    if (!inIframe) {
      console.warn('Dashboard not in iframe - message handler not set up');
      return; // Not in iframe, no need to set up message handler
    }
    
    console.log('Dashboard is in iframe - setting up message handler');

    // Handler for UPDATE_THEME messages
    const handleUpdateTheme = (data, origin) => {
      try {
        console.log('Gallery received UPDATE_THEME message:', { data, origin });
        if (!data || !data.colors) {
          throw new Error('Invalid theme data: missing colors');
        }

        const colors = data.colors;
        const font = data.font || DEFAULT_FONT;

        console.log('Applying theme:', { colors, font });
        // Validate and apply theme
        applyTheme({ colors, font });
        saveTheme(colors, font);

        console.log('Theme applied successfully');
        // Send success confirmation
        sendSuccessToParent(MESSAGE_TYPES.THEME_UPDATED, origin);
      } catch (error) {
        console.error('Error updating theme from iframe:', error);
        sendErrorToParent(error.message, MESSAGE_TYPES.UPDATE_THEME, origin);
      }
    };

    // Handler for UPDATE_LOGO messages
    const handleUpdateLogo = (data, origin) => {
      try {
        if (!data) {
          throw new Error('Invalid logo data');
        }

        // Read current values from localStorage if not provided
        const currentLogoData = loadLogo();
        const logo = data.logo !== undefined ? data.logo : currentLogoData.logo;
        const text = data.text !== undefined ? data.text : currentLogoData.text;

        // Update state and save
        setCurrentLogo(logo);
        setWeddingText(text);
        saveLogo(logo, text);

        // Update document title
        document.title = text;

        // Send success confirmation
        sendSuccessToParent(MESSAGE_TYPES.LOGO_UPDATED, origin);
      } catch (error) {
        console.error('Error updating logo from iframe:', error);
        sendErrorToParent(error.message, MESSAGE_TYPES.UPDATE_LOGO, origin);
      }
    };

    // Handler for UPDATE_TABS messages
    const handleUpdateTabs = (data, origin) => {
      try {
        if (!data) {
          throw new Error('Invalid tabs data');
        }

        // Read current values from localStorage if not provided
        const currentTabsData = loadTabs();
        const floorplan = data.floorplan !== undefined ? data.floorplan : currentTabsData.floorplan;
        const menu = data.menu !== undefined ? data.menu : currentTabsData.menu;
        const showVenueTab = data.showVenueTab !== undefined ? data.showVenueTab : currentTabsData.showVenueTab;
        const showMediaFilter = data.showMediaFilter !== undefined ? data.showMediaFilter : currentTabsData.showMediaFilter;
        const showGuestbookTab = data.showGuestbookTab !== undefined ? data.showGuestbookTab : currentTabsData.showGuestbookTab;
        const showQAndATab = data.showQAndATab !== undefined ? data.showQAndATab : currentTabsData.showQAndATab;

        // Update state and save
        setCurrentFloorplan(floorplan);
        setCurrentMenu(menu || null);
        setShowVenueTab(showVenueTab);
        setShowMediaFilter(showMediaFilter);
        setShowGuestbookTab(showGuestbookTab);
        setShowQAndATab(showQAndATab);
        saveTabs(floorplan, showVenueTab, showMediaFilter, showGuestbookTab, showQAndATab, menu || null);

        // Handle navigation if tabs are hidden - use functional setState to get current activeTab
        setActiveTab((currentActiveTab) => {
          if (!showVenueTab && currentActiveTab === 'venue') {
            navigate(`${dashboardBase}/view`);
            return 'view';
          }
          if (!showGuestbookTab && currentActiveTab === 'guestbook') {
            navigate(`${dashboardBase}/view`);
            return 'view';
          }
          if (!showQAndATab && currentActiveTab === 'qanda') {
            navigate(`${dashboardBase}/view`);
            return 'view';
          }
          return currentActiveTab;
        });

        // Send success confirmation
        sendSuccessToParent(MESSAGE_TYPES.TABS_UPDATED, origin);
      } catch (error) {
        console.error('Error updating tabs from iframe:', error);
        sendErrorToParent(error.message, MESSAGE_TYPES.UPDATE_TABS, origin);
      }
    };

    // Handler for GET_CURRENT_CONFIG messages
    const handleGetCurrentConfig = (origin) => {
      try {
        const theme = loadTheme();
        const logo = loadLogo();
        const tabs = loadTabs();

        sendMessageToParent(
          MESSAGE_TYPES.CONFIG_RESPONSE,
          {
            theme: {
              colors: theme.colors || theme,
              font: theme.font || DEFAULT_FONT,
            },
            logo: {
              logo: logo.logo,
              text: logo.text,
            },
            tabs: {
              floorplan: tabs.floorplan,
              menu: tabs.menu,
              showVenueTab: tabs.showVenueTab,
              showMediaFilter: tabs.showMediaFilter,
              showGuestbookTab: tabs.showGuestbookTab,
              showQAndATab: tabs.showQAndATab,
            },
          },
          origin
        );
      } catch (error) {
        console.error('Error getting current config:', error);
        sendErrorToParent(error.message, MESSAGE_TYPES.GET_CURRENT_CONFIG, origin);
      }
    };

    // Explicit allow-list of parent origins permitted to talk to the gallery.
    const allowedOrigins = getAllowedOrigins();

    // Set up message handler
    const cleanup = createMessageHandler(
      handleUpdateTheme,
      handleUpdateLogo,
      handleUpdateTabs,
      handleGetCurrentConfig,
      allowedOrigins
    );

    // Send ready message to parent. The parent origin is not yet known (no
    // message received), so post the handshake to each explicitly allowed
    // origin rather than broadcasting to '*'.
    const readyPayload = {
      ready: true,
      version: packageJson.version,  // App version (e.g., "1.4.1")
      configVersion: CONFIG_VERSION  // Configuration schema version (centralized)
    };
    const readyTargets = allowedOrigins === '*' ? ['*'] : allowedOrigins;
    readyTargets.forEach((origin) => {
      sendMessageToParent(MESSAGE_TYPES.GALLERY_READY, readyPayload, origin);
    });

    // Cleanup on unmount
    return cleanup;
  }, [inIframe, navigate, dashboardBase]);

  return (
    <div className="min-h-screen bg-surface flex flex-col">
      <header className="pt-5 pb-4 sm:pt-8 sm:pb-6 flex flex-col items-center bg-surface shadow-sm relative">
        <GuestRejoinHelp galleryId={galleryId} />
        {currentLogo && (
          <img
            src={currentLogo}
            alt="Wedding Logo"
            className="w-28 h-28 xs:w-32 xs:h-32 sm:w-48 sm:h-48 object-contain mb-2 sm:mb-3 opacity-95 select-none pointer-events-none"
            draggable="false"
          />
        )}
        <h1 className="text-lg font-medium text-secondary-80 tracking-wide">
          {weddingText}
        </h1>
      </header>

      <nav className="bg-surface border-b border-accent-20 shadow-sm sticky top-0 z-30" role="navigation" aria-label="Main navigation">
        <div className="w-full max-w-4xl mx-auto px-3 sm:px-4 lg:px-6">
          <div className="flex justify-center items-center py-2 sm:py-3">
            {(() => {
              // Calculate number of visible tabs
              const tabCount = 1 + (showVenueTab ? 1 : 0) + (showGuestbookTab ? 1 : 0) + (showQAndATab ? 1 : 0);
              const shouldStack = tabCount >= 3; // Only stack when 3+ tabs
              
              return (
                <div 
                  className="inline-flex items-center justify-center gap-1 sm:gap-2" 
                  role="tablist" 
                  aria-label="Main sections"
                >
                  {/* Conditionally render Venue tab */}
                  {showVenueTab && (
                    <NavigationTab
                      to={`${dashboardBase}/venue`}
                      isActive={activeTab === 'venue'}
                      onClick={() => setActiveTab('venue')}
                      shouldStack={shouldStack}
                      icon={<svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" /></svg>}
                    >
                      Venue
                    </NavigationTab>
                  )}
                  <NavigationTab
                    to={`${dashboardBase}/view`}
                    isActive={activeTab === 'view'}
                    onClick={() => setActiveTab('view')}
                    shouldStack={shouldStack}
                    icon={<svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>}
                  >
                    Gallery
                  </NavigationTab>
                  {/* Conditionally render Guestbook tab */}
                  {showGuestbookTab && (
                    <NavigationTab
                      to={`${dashboardBase}/guestbook`}
                      isActive={activeTab === 'guestbook'}
                      onClick={() => setActiveTab('guestbook')}
                      shouldStack={shouldStack}
                      icon={<svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>}
                    >
                      Guestbook
                    </NavigationTab>
                  )}
                  {/* Conditionally render Q&A tab */}
                  {showQAndATab && (
                    <NavigationTab
                      to={`${dashboardBase}/qanda`}
                      isActive={activeTab === 'qanda'}
                      onClick={() => setActiveTab('qanda')}
                      shouldStack={shouldStack}
                      icon={<svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
                    >
                      Q&A
                    </NavigationTab>
                  )}
                </div>
              );
            })()}
          </div>
        </div>
      </nav>
      <main className="flex-grow" role="main">
        <Routes>
          {/*
            Descendant <Routes> match the pathname *after* the parent `dashboard/*` segment
            (e.g. `/view`), not the full `/g/:id/dashboard/view`. Use leaf segments only.
          */}
          <Route index element={<Navigate to={`${dashboardBase}/view`} replace />} />
          <Route
            path="view"
            element={
              <div id="gallery-panel" role="tabpanel" aria-labelledby="gallery-tab">
                <PhotoGallery
                  selectionMode={selectionMode}
                  setSelectionMode={setSelectionMode}
                  refreshTrigger={refreshTrigger}
                  showMediaFilter={showMediaFilter}
                  pendingUploadPreviews={pendingUploadPreviews}
                />
              </div>
            }
          />
          <Route
            path="venue"
            element={
              <div id="venue-panel" role="tabpanel" aria-labelledby="venue-tab">
                <Venue customFloorplan={currentFloorplan} customMenu={currentMenu} />
              </div>
            }
          />
          <Route
            path="guestbook"
            element={
              <div id="guestbook-panel" role="tabpanel" aria-labelledby="guestbook-tab">
                <Guestbook />
              </div>
            }
          />
          <Route
            path="qanda"
            element={
              <div id="qanda-panel" role="tabpanel" aria-labelledby="qanda-tab">
                <QAndA />
              </div>
            }
          />
          <Route
            path="upload"
            element={
              <div id="upload-panel" role="tabpanel" aria-labelledby="upload-tab" className="px-4 py-6">
                <UploadForm
                  onUploadTabActivate={handleUploadTabActivate}
                  onUploadComplete={handleUploadComplete}
                />
              </div>
            }
          />
          <Route
            path="*"
            element={<Navigate to={`${dashboardBase}/view`} replace />}
          />
        </Routes>
      </main>

      <OfflineUploadStatus />

      {/* Toast Notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default Dashboard;
